import {useMemo} from 'react';
import {useMatch} from 'react-router';
import {useWhiteboardMetadata} from '../features/whiteboards/hooks/useWhiteboardMetadata';
import {
    filterLinksByType,
    normalizeWhiteboardLinks,
    WHITEBOARD_LINK_TYPES,
} from '../features/whiteboards/utils/whiteboardLinks';

/**
 * Chart scope for the AI Assistant when the route is `/whiteboards/:whiteboardId`.
 * Mirrors `useChartTeamContext` (team tab binding) but returns a value for agentic
 * `context.chart` instead of writing CommunicationHub `teamContext`.
 *
 * Includes `linkedProducts` from whiteboard metadata links so Chart AI can ground
 * suggest-missing / Q&A against associated reports (backend also reloads links from DB).
 *
 * @returns {{chartId: string, title: string, linkedProducts: Array<{productId: string, title?: string, productType?: string}>}|null}
 */
export default function useChartAiContext() {
    const match = useMatch('/charts/:whiteboardId');
    const whiteboardId = match?.params?.whiteboardId || null;
    const {whiteboard, metadataLoaded} = useWhiteboardMetadata(whiteboardId);

    return useMemo(() => {
        if (!whiteboardId) {
            return null;
        }
        const title = metadataLoaded
            ? (whiteboard?.title || 'Untitled')
            : (whiteboard?.title || '');
        const productLinks = filterLinksByType(
            normalizeWhiteboardLinks(whiteboard || {}),
            WHITEBOARD_LINK_TYPES.PRODUCT,
        );
        const linkedProducts = productLinks.map((link) => ({
            productId: link.targetId,
            title: link.title || link.targetId,
            productType: link.productType || null,
        }));
        return {
            chartId: whiteboardId,
            title,
            linkedProducts,
        };
    }, [whiteboardId, whiteboard, metadataLoaded]);
}
